import type { ErrorLike } from '@apollo/client';
import { Button } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';

interface RestoreSessionModalProps {
  postcode?: Nullable<string>;
  error?: ErrorLike;
  isLoading: boolean;
  isOpen: boolean;
  onProceed: () => void;
  onStartAgain: () => void;
}

interface RestoreSessionModalUIProps {
  dialogId: DialogID;
  postcode: string;
  onProceed: () => void;
  onStartAgain: () => void;
  isLoading: boolean;
}

interface RestoreSessionErrorModalUIProps {
  dialogId: DialogID;
  onStartAgain: () => void;
  onRefresh: () => void;
  isLoading: boolean;
}

const RestoreSessionErrorModalUI = ({
  dialogId,
  onStartAgain,
  onRefresh,
  isLoading,
}: RestoreSessionErrorModalUIProps) => (
  <Dialog
    id={dialogId}
    heading="Oops! An error has occurred while loading your quote"
    description="Something went wrong while loading your quote. Start again or refresh the page"
    hideCloseButton
    preventDismiss
    data-testid="restoreSessionErrorModal"
    loading={isLoading}
    loadingText="Loading your quote..."
  >
    <DialogActions>
      <Button variant="ghost" colorScheme="destructive" onClick={onStartAgain}>
        Start again
      </Button>
      <Button variant="solid" onClick={onRefresh}>
        Refresh
      </Button>
    </DialogActions>
  </Dialog>
);

const RestoreSessionModalUI = ({
  dialogId,
  postcode,
  isLoading,
  onProceed,
  onStartAgain,
}: RestoreSessionModalUIProps) => (
  <Dialog
    id={dialogId}
    heading={`Continue your quote for ${postcode}`}
    description="Pick up where you left off or start a new quote"
    hideCloseButton
    preventDismiss
    data-testid="restoreSessionModal"
    loading={isLoading}
    loadingText="Loading your quote..."
  >
    <DialogActions>
      <Button
        data-testid="start-again-button"
        variant="ghost"
        colorScheme="destructive"
        onClick={onStartAgain}
      >
        Start again
      </Button>
      <Button
        data-testid="continue-my-quote-button"
        variant="solid"
        onClick={onProceed}
      >
        Continue my quote
      </Button>
    </DialogActions>
  </Dialog>
);

const RestoreSessionModal = ({
  postcode,
  error,
  isLoading,
  isOpen,
  onProceed,
  onStartAgain,
}: RestoreSessionModalProps) => {
  const dialog = useDialog(DialogID.RESTORE_SESSION);
  const navigate = useNavigate();

  useEffect(() => {
    dialog.setOpen(isOpen);
  }, [dialog, isOpen]);

  if (error || !postcode) {
    return (
      <RestoreSessionErrorModalUI
        dialogId={dialog.id}
        onStartAgain={onStartAgain}
        onRefresh={() => navigate(0)}
        isLoading={isLoading}
      />
    );
  }

  return (
    <RestoreSessionModalUI
      dialogId={dialog.id}
      postcode={postcode}
      onProceed={onProceed}
      onStartAgain={onStartAgain}
      isLoading={isLoading}
    />
  );
};

export type {
  RestoreSessionModalProps,
  RestoreSessionModalUIProps,
  RestoreSessionErrorModalUIProps,
};

export {
  RestoreSessionModal,
  RestoreSessionModalUI,
  RestoreSessionErrorModalUI,
};
